<template>
  <div>
    <h1 :class="$style.alfhabetTitle">{{ data.gamesTitle.toUpperCase() }}</h1>
    <h2 :class="$style.alfhabetSubtitle">{{ data.gamesSubtitle }}</h2>
    <div :class="$style.alphabetTopHead">
      <ul :class="$style.alphabetList">
        <li v-for="letter in alphabet" :key="letter">
          <a :href="`#${letter}`">{{ letter }}</a>
        </li>
      </ul>
    </div>
    <div v-for="(games, letter) in gamesByAlphabet" :key="letter" :class="$style.alphabet">
      <div v-if="games.length">
        <div :id="String(letter)" :class="$style.alphabetIdentifier">
          <a :id="String(letter)">{{ letter }}</a>
          <span>{{ games.length }} {{ data.gamesCountTitle }}</span>
        </div>
        <ul :class="$style.alphabetResult">
          <li v-for="game in games" :key="game.id">
            <NuxtLink :to="game.strapiUrl"> {{ game.title }} </NuxtLink>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, onMounted, ref } from 'vue';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsGames } from '~/stores/softswiss/games';
import { useStoreGamesStrapi } from '~/stores/strapi/games';
import type { ComponentInfoPageBlocksAlphabetBlock } from '~/types/infoPages';
import type { IGamesRequestOptions, ISsGame } from '~/types/softswiss/ssGames';
interface SsGameInterfaceExtended extends ISsGame {
  strapiUrl?: string;
}

defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksAlphabetBlock>,
    required: true,
  },
});

const router = useRouter();
const route = useRoute();
const storeRoot = useStoreRoot();
const storeGames = useStoreGamesStrapi();
const storeSsGames = useStoreSsGames();

const gamesDataList = ref<{ data?: ISsGame[]; meta?: any }>({});

const alphabet = [...Array(26)].map((_, i) => String.fromCharCode(i + 65));
alphabet.unshift('0-9');

const langPrefix = computed(() => {
  return storeRoot.domainLang ? `/${storeRoot.domainLang}` : '';
});

const loadDataGames = async () => {
  const params: IGamesRequestOptions = {
    ids: storeGames.games.map((game) => game.gameId),
  };

  gamesDataList.value = await storeSsGames.loadGames(params);
};

const gamesByAlphabet = computed(() => {
  const gamesByAlphabet: { [key: string]: SsGameInterfaceExtended[] } = {};
  alphabet.forEach((sign) => (gamesByAlphabet[sign] = []));

  storeGames.games.forEach((game) => {
    const ssGame = gamesDataList.value?.data?.find(
      (g) => g.externalId === game.gameId
    ) as SsGameInterfaceExtended;

    if (ssGame) {
      const firstSign = ssGame.title[0].toUpperCase();
      const isNumber = !isNaN(Number(firstSign));

      ssGame.strapiUrl = `${langPrefix.value}/games${game.url}`;

      if (isNumber) {
        gamesByAlphabet['0-9'].push(ssGame);
      } else {
        gamesByAlphabet[firstSign].push(ssGame);
      }
    }
  });

  return gamesByAlphabet;
});

onBeforeMount(() => storeGames.loadGamesInfo());

onMounted(async () => {
  await loadDataGames();

  if (route.hash) {
    router.replace({
      query: { ...route.query },
      hash: '',
    });
  }
});
</script>

<style module lang="scss">
.alfhabetTitle {
  letter-spacing: 0.5px;
  @include textResposive_Medium();
}

.alfhabetSubtitle {
  color: rgb(255 255 255 / 70%);
  margin-bottom: 40px;
  @include textResposive_Text();
}

.alphabetIdentifier {
  padding: 13px 27px 13px 24px;
  display: flex;
  border-radius: 16px;
  margin-bottom: 24px;
  background-color: $color-background-container;
  align-items: center;
  justify-content: space-between;

  span {
    &:first-child {
      font-weight: 800;
      font-size: 28px;
      color: $color-primary-default;
    }

    &:last-child {
      font-style: italic;
      color: rgb(255 255 255 / 70%);
      @include textResposive_Small();
    }
  }
}

.alphabet {
  margin-bottom: 35px;
}

.alphabetResult {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;

  li {
    width: 33.3%;
    padding: 12px 16px;
    margin: 0;
    @include textResposive_Small();
  }

  a {
    color: $color-text-color-basic-primary;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      color: $color-primary-hover;
    }
  }
}

.alphabetList {
  padding: 5px 12px;
  margin: 0;
  display: flex;
  border-radius: 16px 16px 0 0;
  border-bottom: 2px solid #0b0e2c;
  flex-wrap: wrap;
  justify-content: space-between;
  background-color: #181637;

  li {
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    cursor: pointer;
    justify-content: center;
    list-style: none;
    margin: 0;

    color: rgb(255 255 255 / 40%);
    letter-spacing: 2px;
    text-transform: uppercase;
    @include textResposive_Small();

    &.active,
    &:hover {
      color: $color-primary-hover;
    }

    &::marker {
      color: transparent;
    }
  }
}

.alphabetTopHead {
  + .alphabet {
    .alphabetIdentifier {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }
}

// ***********************************************************//
// 00. Responsive code
// ***********************************************************//
@media screen and (width <= 1247px) {
  .alphabetList {
    justify-content: center;
  }

  .alphabetResult {
    li {
      width: 50%;
    }
  }
}

:global(.isMobile) {
  .alphabetResult {
    li {
      width: 100%;
      padding: 12px 0 12px 20px;
    }
  }
}
</style>
